const EVO_MENU_EASE_OUT = "cubic-bezier(0.2, 0.7, 0.2, 1)";
const EVO_MENU_EASE_IN = "cubic-bezier(0.4, 0, 1, 1)";
const EVO_MENU_SHEET = "cubic-bezier(0.32, 0.72, 0, 1)";
const evoMenuAnimated = () => !window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * EVO Shop-Menü (mobile drawer)
 * - Segmented toggle between "Nach Kategorie" / "Nach Ziel"
 * - In-place tile drilldown with "back to categories"
 * - Resets to initial state whenever the drawer is closed
 */
class ShopMenuMobile extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector(".shop-menu-mobile__toggle");
    this.tabButtons = Array.from(this.querySelectorAll(".shop-menu-mobile__tab-btn"));
    this.tabPanels = Array.from(this.querySelectorAll(".shop-menu-mobile__tab"));

    this.addEventListener("click", this._onClick.bind(this));
    this.addEventListener("keydown", this._onKeydown.bind(this));

    const drawer = this.closest("navigation-drawer");
    if (drawer) {
      drawer.addEventListener("dialog:after-hide", () => this.reset());

      // "Warenkorb" in the fixed bottom bar opens the cart drawer (theme handler via aria-controls): close the menu behind it
      drawer.addEventListener("click", (event) => {
        if (event.target.closest("[data-shop-menu-cart][aria-controls]")) {
          drawer.hide();
        }
      });
    }

    this._syncIndicator();
  }

  get activeIndex() {
    return Math.max(
      0,
      this.tabButtons.findIndex((button) => button.getAttribute("aria-selected") === "true")
    );
  }

  _onClick(event) {
    const tabButton = event.target.closest(".shop-menu-mobile__tab-btn");
    if (tabButton && this.contains(tabButton)) {
      const index = this.tabButtons.indexOf(tabButton);
      if (index === this.activeIndex) return;
      this._animateSwap(() => this.selectTab(index), index > this.activeIndex ? 1 : -1, false);
      return;
    }

    const tile = event.target.closest("button[data-drilldown]");
    if (tile && this.contains(tile)) {
      event.preventDefault();
      this._animateSwap(() => this.openDrilldown(tile.getAttribute("data-drilldown")), 1);
      return;
    }

    const back = event.target.closest(".shop-menu-drilldown__back");
    if (back && this.contains(back)) {
      event.preventDefault();
      this._animateSwap(() => this.closeDrilldown(back.closest(".shop-menu-drilldown")), -1);
    }
  }

  /* everything visible in the list, top to bottom (search + toggle only with chrome) */
  motionRows(chrome = true) {
    const shown = (element) => element && element.getClientRects().length > 0;
    const rows = chrome
      ? [this.querySelector(":scope > .shop-menu-search"), this.querySelector(":scope > .shop-menu-mobile__toggle")]
      : [];
    const tab = this.tabPanels.find((panel) => !panel.hidden);
    const drilldown = tab && tab.querySelector(".shop-menu-drilldown:not([hidden])");
    if (drilldown) {
      rows.push(drilldown.querySelector(".shop-menu-drilldown__bar"), ...drilldown.querySelectorAll(".shop-menu-drilldown__list > *"));
    } else if (tab) {
      rows.push(...tab.querySelectorAll(".shop-menu-mobile__grid > *"));
    }
    return rows.filter(shown);
  }

  /*
   * Old rows slide out (direction 1 = to the left), the change happens, new rows follow one after another from the
   * other side. With reduced motion the change happens at once.
   */
  _animateSwap(change, direction, chrome = true) {
    if (!evoMenuAnimated()) {
      change();
      return;
    }
    if (this._swapping) return;
    this._swapping = true;
    (this._swapAnimations || []).forEach((animation) => animation.cancel());

    const outgoing = this.motionRows(chrome).map((row) =>
      row.animate(
        { opacity: [1, 0], transform: ["none", `translateX(${-16 * direction}px)`] },
        { duration: 130, easing: EVO_MENU_EASE_IN, fill: "forwards" }
      )
    );

    Promise.all(outgoing.map((animation) => animation.finished))
      .catch(() => {})
      .then(() => {
        change();
        outgoing.forEach((animation) => animation.cancel());
        this._swapAnimations = this.motionRows(chrome).map((row, index) =>
          row.animate(
            { opacity: [0, 1], transform: [`translateX(${24 * direction}px)`, "none"] },
            { duration: 360, delay: Math.min(index, 12) * 30, easing: EVO_MENU_EASE_OUT, fill: "backwards" }
          )
        );
        this._swapping = false;
      });
  }

  _onKeydown(event) {
    const tabButton = event.target.closest(".shop-menu-mobile__tab-btn");
    if (!tabButton) return;

    let nextIndex = null;
    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      nextIndex = (this.activeIndex + 1) % this.tabButtons.length;
    } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      nextIndex = (this.activeIndex - 1 + this.tabButtons.length) % this.tabButtons.length;
    } else if (event.key === "Home") {
      nextIndex = 0;
    } else if (event.key === "End") {
      nextIndex = this.tabButtons.length - 1;
    }

    if (nextIndex !== null) {
      event.preventDefault();
      this.selectTab(nextIndex);
      this.tabButtons[nextIndex].focus();
    }
  }

  selectTab(index) {
    if (index < 0 || index >= this.tabButtons.length) return;

    this.tabButtons.forEach((button, buttonIndex) => {
      const isActive = buttonIndex === index;
      button.setAttribute("aria-selected", isActive ? "true" : "false");
      button.setAttribute("tabindex", isActive ? "0" : "-1");
    });

    this.tabPanels.forEach((panel, panelIndex) => {
      if (panelIndex === index) {
        panel.removeAttribute("hidden");
      } else {
        panel.setAttribute("hidden", "");
      }
    });

    this._closeAllDrilldowns();
    this._syncIndicator();
    this._scrollToTop();
  }

  _closeAllDrilldowns() {
    this.querySelectorAll(".shop-menu-drilldown").forEach((item) => item.setAttribute("hidden", ""));
    this.querySelectorAll("button[data-drilldown][aria-expanded]").forEach((tile) =>
      tile.setAttribute("aria-expanded", "false")
    );
    this.tabPanels.forEach((tab) => tab.classList.remove("is-drilldown-open"));
    this.classList.remove("is-drilldown-open");
  }

  _syncIndicator() {
    if (!this.toggle) return;
    this.toggle.style.setProperty("--shop-menu-tab-count", String(Math.max(1, this.tabButtons.length)));
    this.toggle.style.setProperty("--shop-menu-tab-index", String(this.activeIndex));
  }

  openDrilldown(id) {
    const drilldown = id ? this.querySelector(`#${CSS.escape(id)}`) : null;
    if (!drilldown) return;

    const tab = drilldown.closest(".shop-menu-mobile__tab");
    if (tab) {
      tab.querySelectorAll(".shop-menu-drilldown").forEach((item) => item.setAttribute("hidden", ""));
      tab.classList.add("is-drilldown-open");
    }
    this.classList.add("is-drilldown-open");

    drilldown.removeAttribute("hidden");
    this._setTileExpanded(id, true);
    this._scrollToTop();

    const back = drilldown.querySelector(".shop-menu-drilldown__back");
    if (back) {
      back.focus({ preventScroll: true });
    }
  }

  closeDrilldown(drilldown) {
    if (!drilldown) return;
    const tab = drilldown.closest(".shop-menu-mobile__tab");
    const tileId = drilldown.id;

    drilldown.setAttribute("hidden", "");
    if (tab) {
      tab.classList.remove("is-drilldown-open");
    }
    this.classList.remove("is-drilldown-open");

    this._scrollToTop();

    const tile = this._setTileExpanded(tileId, false);
    if (tile) {
      tile.focus({ preventScroll: true });
    }
  }

  _setTileExpanded(id, expanded) {
    const tile = id ? this.querySelector(`button[data-drilldown="${CSS.escape(id)}"]`) : null;
    if (tile) {
      tile.setAttribute("aria-expanded", expanded ? "true" : "false");
    }
    return tile;
  }

  _scrollToTop() {
    // the list scrolls inside .panel__scroller (the bottom bar sits below it); older markup scrolled .panel__wrapper
    const scroller = this.closest(".panel__scroller") || this.closest(".panel__wrapper");
    if (scroller) {
      scroller.scrollTo({ top: 0, behavior: "auto" });
    }
  }

  reset() {
    this.querySelectorAll("details.shop-menu-accordion[open]").forEach((details) => details.removeAttribute("open"));
    this.selectTab(0);
  }
}

if (!window.customElements.get("shop-menu-mobile")) {
  window.customElements.define("shop-menu-mobile", ShopMenuMobile);
}

/*
 * Open/close of the shop menu drawer: the sheet slides in with its content already on it, the rows settle one after
 * another slightly behind it. Replaces the theme's transition (Drawer in theme.js: dim, then an empty white sheet
 * wipes in via clip-path, then all content at once); with reduced motion the theme's transition stays.
 */
(() => {
  const parts = (drawer) => {
    const root = drawer.shadowRoot;
    return {
      content: root.querySelector('[part="content"]'),
      overlay: root.querySelector('[part="overlay"]'),
      outside: root.querySelector('[part="outside-close-button"]'),
    };
  };

  const cancel = (drawer) => {
    (drawer._evoMenuAnimations || []).forEach((animation) => animation.cancel());
    drawer._evoMenuAnimations = [];
  };

  const settle = (animations) => Promise.all(animations.map((animation) => animation.finished)).catch(() => {});

  const show = (drawer, animate) => {
    const { content, overlay, outside } = parts(drawer);
    cancel(drawer);
    drawer._setInitialPosition();

    // the theme transition may have left inline styles behind (clip-path, opacity)
    [drawer, content, overlay, outside, ...content.children].forEach((element) => {
      if (!element) return;
      ["opacity", "visibility", "clip-path", "transform"].forEach((property) => element.style.removeProperty(property));
    });
    drawer.style.visibility = "visible";
    drawer.style.opacity = "1";

    if (!animate) {
      drawer.classList.add("show-close-cursor");
      return Promise.resolve();
    }

    const menu = drawer.querySelector("shop-menu-mobile");
    const rows = [
      drawer.querySelector(".shop-menu-drawer__header"),
      ...(menu && menu.motionRows ? menu.motionRows() : []),
      drawer.querySelector(".shop-menu-bar"),
    ].filter(Boolean);

    const animations = [
      overlay.animate({ opacity: [0, 1] }, { duration: 240, easing: "linear" }),
      content.animate({ transform: ["translateX(-100%)", "none"] }, { duration: 420, easing: EVO_MENU_SHEET }),
    ];
    rows.forEach((row, index) =>
      animations.push(
        row.animate(
          { opacity: [0, 1], transform: ["translateX(-24px)", "none"] },
          { duration: 380, delay: 60 + Math.min(index, 12) * 22, easing: EVO_MENU_EASE_OUT, fill: "backwards" }
        )
      )
    );

    drawer._evoMenuAnimations = animations;
    return settle(animations).then(() => drawer.classList.add("show-close-cursor"));
  };

  const hide = (drawer) => {
    const { content, overlay } = parts(drawer);
    cancel(drawer);

    const animations = [
      content.animate({ transform: ["none", "translateX(-100%)"] }, { duration: 280, easing: EVO_MENU_EASE_IN, fill: "forwards" }),
      overlay.animate({ opacity: [1, 0] }, { duration: 280, easing: "linear", fill: "forwards" }),
    ];

    drawer._evoMenuAnimations = animations;
    return settle(animations).then(() => {
      if (drawer.hasAttribute("open")) return; // opened again meanwhile
      drawer.style.visibility = "hidden";
      drawer.style.opacity = "0";
      animations.forEach((animation) => animation.cancel());
      drawer.classList.remove("show-close-cursor");
    });
  };

  window.customElements.whenDefined("navigation-drawer").then(() => {
    const proto = window.customElements.get("navigation-drawer").prototype;
    if (proto._evoMenuPatched) return;
    proto._evoMenuPatched = true;

    const baseShow = proto._showTransition;
    const baseHide = proto._hideTransition;
    const isShopMenu = (drawer) => evoMenuAnimated() && drawer.classList.contains("navigation-drawer--shop-menu");

    proto._showTransition = function (animate = true) {
      return isShopMenu(this) ? show(this, animate) : baseShow.call(this, animate);
    };
    proto._hideTransition = function () {
      return isShopMenu(this) ? hide(this) : baseHide.call(this);
    };
  });
})();

/**
 * EVO "Produkte" mega menu (desktop)
 * - Sidebar with categories; hover/focus on an item swaps the panel on the right
 * - The menu takes the height of the open panel (at least the sidebar), animated on switch
 * - Resets to the first category whenever the parent <details> closes
 */
class ShopProductsMega extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll("[data-cat-tab]"));
    this.panels = Array.from(this.querySelectorAll("[data-cat-panel]"));
    if (this.tabs.length === 0) return;

    this.tabs.forEach((tab) => {
      const id = tab.getAttribute("data-cat-tab");
      tab.addEventListener("pointerenter", () => this.activate(id));
      tab.addEventListener("focus", () => this.activate(id));
    });

    const details = this.closest("details");
    if (details) {
      details.addEventListener("toggle", () => {
        if (!details.open) this.activate(this.tabs[0].getAttribute("data-cat-tab"));
      });
    }
  }

  activate(id) {
    if (!id || id === this._activeId) return;
    this._activeId = id;

    // the menu is as tall as the open category: glide between the heights instead of jumping
    const from = this.offsetHeight;

    this.tabs.forEach((tab) => {
      tab.classList.toggle("is-active", tab.getAttribute("data-cat-tab") === id);
    });

    this.panels.forEach((panel) => {
      const isActive = panel.getAttribute("data-cat-panel") === id;
      panel.classList.toggle("is-active", isActive);
      if (isActive) {
        panel.removeAttribute("aria-hidden");
        panel.querySelectorAll('img[loading="lazy"]').forEach((image) => image.setAttribute("loading", "eager"));
      } else {
        panel.setAttribute("aria-hidden", "true");
      }
    });

    const to = this.offsetHeight;
    if (from > 0 && to > 0 && from !== to && !window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      this._heightAnimation?.cancel();
      this._heightAnimation = this.animate(
        [
          { height: `${from}px`, overflow: "hidden" },
          { height: `${to}px`, overflow: "hidden" },
        ],
        { duration: 220, easing: "cubic-bezier(0.2, 0.7, 0.2, 1)" }
      );
    }
  }
}

if (!window.customElements.get("shop-products-mega")) {
  window.customElements.define("shop-products-mega", ShopProductsMega);
}

/*
 * Mobile menu search: live results while typing (Shopify predictive search, /search/suggest.json).
 * While there is a query the category/goal toggle and lists are hidden (.shop-menu-mobile.is-searching).
 */
(() => {
  if (window.evoMenuSearch) return;
  window.evoMenuSearch = true;

  const escapeHtml = (value) => String(value ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
  const money = (value) => {
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || "EUR";
    try {
      return new Intl.NumberFormat(document.documentElement.lang || "de", { style: "currency", currency }).format(Number(value));
    } catch (e) {
      return value;
    }
  };

  let timer = null;
  let controller = null;

  const render = (menu, box, query, data) => {
    const products = data?.resources?.results?.products || [];
    const collections = data?.resources?.results?.collections || [];
    const searchUrl = `${(window.Shopify?.routes?.root || "/")}search?type=product&q=${encodeURIComponent(query)}`;

    if (!products.length && !collections.length) {
      box.innerHTML = `<p class="shop-menu-search-results__empty">${escapeHtml(box.dataset.emptyLabel)}</p>`;
      return;
    }

    let html = "";
    if (collections.length) {
      html += '<ul class="shop-menu-search-results__collections" role="list">' + collections.slice(0, 4).map((c) =>
        `<li><a class="shop-menu-search-results__collection" href="${escapeHtml(c.url)}">${escapeHtml(c.title)}</a></li>`).join("") + "</ul>";
    }
    if (products.length) {
      html += '<ul class="shop-menu-search-results__products" role="list">' + products.map((p) => {
        /* price_max: the regular pack – price/price_min is often a trial size (e.g. 30 g) */
        const img = p.image ? `<img src="${escapeHtml(p.image)}${p.image.includes("?") ? "&" : "?"}width=120" alt="" width="48" height="48" loading="lazy">` : "";
        return `<li><a class="shop-menu-search-results__product" href="${escapeHtml(p.url)}">
          <span class="shop-menu-search-results__media">${img}</span>
          <span class="shop-menu-search-results__body">
            <span class="shop-menu-search-results__title">${escapeHtml(p.title)}</span>
            <span class="shop-menu-search-results__price">${escapeHtml(money(p.price_max || p.price))}</span>
          </span>
        </a></li>`;
      }).join("") + "</ul>";
    }
    html += `<a class="shop-menu-search-results__all" href="${escapeHtml(searchUrl)}">${escapeHtml(box.dataset.allLabel)}</a>`;
    box.innerHTML = html;
  };

  /* x button: empty the field, reset the live results (via the input handler below) and keep typing */
  document.addEventListener("click", (event) => {
    const clear = event.target.closest?.(".shop-menu-search__clear");
    if (!clear) return;
    const input = clear.parentElement.querySelector(".shop-menu-search__input");
    if (!input) return;
    input.value = "";
    input.dispatchEvent(new Event("input", { bubbles: true }));
    input.focus();
  });

  document.addEventListener("input", (event) => {
    const input = event.target.closest?.(".shop-menu-search__input");
    if (!input) return;
    const menu = input.closest("shop-menu-mobile");
    const box = menu?.querySelector("[data-search-results]");
    if (!menu || !box) return;

    const query = input.value.trim();
    clearTimeout(timer);

    if (query.length < 2) {
      menu.classList.remove("is-searching");
      box.hidden = true;
      box.innerHTML = "";
      return;
    }

    menu.classList.add("is-searching");
    box.hidden = false;

    timer = setTimeout(async () => {
      controller?.abort();
      controller = new AbortController();
      const root = window.Shopify?.routes?.root || "/";
      const url = `${root}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,collection&resources[limit]=6&resources[options][unavailable_products]=last`;
      try {
        const response = await fetch(url, { signal: controller.signal });
        const data = await response.json();
        if (input.value.trim() === query) render(menu, box, query, data);
      } catch (error) {
        if (error.name !== "AbortError") box.innerHTML = "";
      }
    }, 200);
  });
})();
